import { useCallback, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  updateQuoteMutation,
  QuoteDTO
} from '@setvi/shared/services/react-query';
import { useSharedMutationQueue } from './use-mutation-queue';

interface UpdateQuoteParams {
  quoteId: string;
  body: any;
}

/**
 * Hook that provides a queued version of updateQuoteMutation
 *
 * This ensures that only one update mutation runs at a time, preventing
 * race conditions when multiple components try to update the quote simultaneously.
 *
 * All calls to mutate/mutateAsync will be queued and executed sequentially.
 */
export const useQueuedUpdateMutation = () => {
  const queue = useSharedMutationQueue();
  const mutation = useMutation(updateQuoteMutation());

  // Wrap mutateAsync to use the queue
  const queuedMutateAsync = useCallback(
    async (params: UpdateQuoteParams): Promise<QuoteDTO> =>
      queue.enqueue(() => mutation.mutateAsync(params)),
    [queue, mutation]
  );

  // Wrap mutate to use the queue (fire-and-forget version)
  const queuedMutate = useCallback(
    (params: UpdateQuoteParams) => {
      queue
        .enqueue(() => mutation.mutateAsync(params))
        .catch(() => {
          // Error is already handled by mutation's onError
        });
    },
    [queue, mutation]
  );

  return useMemo(
    () => ({
      mutate: queuedMutate,
      mutateAsync: queuedMutateAsync,
      isLoading: mutation.isLoading || queue.isProcessing,
      isError: mutation.isError,
      isSuccess: mutation.isSuccess,
      error: mutation.error,
      data: mutation.data,
      reset: mutation.reset,
      queueLength: queue.queueLength,
      hasQueuedMutations: queue.hasQueuedMutations,
      setOnQueueEmpty: queue.setOnQueueEmpty
    }),
    [
      queuedMutate,
      queuedMutateAsync,
      mutation.isLoading,
      mutation.isError,
      mutation.isSuccess,
      mutation.error,
      mutation.data,
      mutation.reset,
      queue.isProcessing,
      queue.queueLength,
      queue.hasQueuedMutations,
      queue.setOnQueueEmpty
    ]
  );
};
